import React, { useCallback, useEffect, useState } from "react";
import { ChevronDown, History, RotateCcw, Upload } from "lucide-react";
import { API, Badge, Button, ConfirmModal, Drawer, EmptyState, Skeleton, apiFetch, cx } from "../../pages/admin/_shared";
import { usePermission } from "../../hooks/usePermission";

const KIND = {
  baseline: { label: "Ban đầu", tone: "neutral" },
  save: { label: "Lưu", tone: "primary" },
  upload: { label: "Thay tệp", tone: "info" },
  restore: { label: "Khôi phục", tone: "warning" },
};

const fmt = (iso) => new Date(iso).toLocaleString("vi-VN", { day: "2-digit", month: "2-digit", year: "numeric", hour: "2-digit", minute: "2-digit" });

function VersionRow({ v, isCurrent, canRestore, onRestore }) {
  const [open, setOpen] = useState(false);
  const [diff, setDiff] = useState(null);
  const [loading, setLoading] = useState(false);
  const k = KIND[v.kind] || KIND.save;

  const toggle = async () => {
    const next = !open;
    setOpen(next);
    if (next && !diff) {
      setLoading(true);
      try {
        setDiff((await apiFetch(`${API}/settings/versions/${v._id}`)).diff || []);
      } catch {
        setDiff([]);
      } finally {
        setLoading(false);
      }
    }
  };

  return (
    <li className="rounded-xl border border-admin-border bg-admin-surface">
      <div className="flex items-start gap-3 p-3.5">
        <span className="mt-0.5 flex h-8 min-w-[2rem] items-center justify-center rounded-lg bg-admin-muted px-1.5 text-xs font-bold tabular-nums text-admin-text">v{v.version}</span>
        <div className="min-w-0 flex-1">
          <div className="flex flex-wrap items-center gap-2">
            <Badge tone={k.tone}>{k.label}</Badge>
            {isCurrent ? <Badge tone="success" dot>Hiện tại</Badge> : null}
          </div>
          <p className="mt-1 text-sm font-medium text-admin-text">{v.summary || "—"}</p>
          <p className="mt-0.5 text-xs text-admin-textMuted">
            {fmt(v.createdAt)}
            {v.createdByName ? ` · ${v.createdByName}` : ""}
          </p>
          {v.note ? <p className="mt-1 rounded-md bg-admin-muted px-2 py-1 text-xs text-admin-text">{v.note}</p> : null}
        </div>
      </div>
      {!isCurrent ? (
        <div className="flex items-center justify-between gap-2 border-t border-admin-border px-3.5 py-2">
          <button type="button" onClick={toggle} aria-expanded={open} className="inline-flex items-center gap-1 text-xs font-semibold text-admin-primary hover:underline">
            <ChevronDown size={13} className={cx("transition", open && "rotate-180")} />
            So sánh với hiện tại
          </button>
          {canRestore ? (
            <Button size="sm" variant="secondary" icon={RotateCcw} onClick={() => onRestore(v)}>
              Khôi phục
            </Button>
          ) : null}
        </div>
      ) : null}
      {open ? (
        <div className="border-t border-admin-border bg-admin-muted/40 px-3.5 py-3">
          {loading ? (
            <Skeleton className="h-10 w-full" />
          ) : !diff?.length ? (
            <p className="text-xs text-admin-textMuted">Không có khác biệt so với hiện tại.</p>
          ) : (
            <ul className="space-y-3">
              {diff.map((d) => (
                <li key={d.field}>
                  <p className="text-xs font-semibold text-admin-text">{d.label}</p>
                  <div className="mt-1 grid grid-cols-1 gap-1.5 sm:grid-cols-2">
                    <p className="break-words rounded-md bg-emerald-50 px-2 py-1.5 text-[11px] text-emerald-800">
                      <span className="mb-0.5 block font-semibold">Phiên bản này</span>
                      {d.version}
                    </p>
                    <p className="break-words rounded-md bg-rose-50 px-2 py-1.5 text-[11px] text-rose-800">
                      <span className="mb-0.5 block font-semibold">Hiện tại</span>
                      {d.current}
                    </p>
                  </div>
                </li>
              ))}
            </ul>
          )}
        </div>
      ) : null}
    </li>
  );
}

/** Lịch sử phiên bản cài đặt website: xem, so sánh với hiện tại và khôi phục. */
export default function SettingsHistoryDrawer({ open, onClose, onRestored }) {
  const { can } = usePermission();
  const [state, setState] = useState({ loading: true, versions: [], error: "" });
  const [confirm, setConfirm] = useState(null);
  const [restoring, setRestoring] = useState(false);

  const load = useCallback(async () => {
    setState((s) => ({ ...s, loading: true, error: "" }));
    try {
      const res = await apiFetch(`${API}/settings/versions`);
      setState({ loading: false, versions: res.versions || [], error: "" });
    } catch (e) {
      setState({ loading: false, versions: [], error: e?.message || "Không tải được lịch sử" });
    }
  }, []);

  useEffect(() => {
    if (open) load();
  }, [open, load]);

  const doRestore = async () => {
    setRestoring(true);
    try {
      await apiFetch(`${API}/settings/versions/${confirm._id}/restore`, { method: "POST" });
      setConfirm(null);
      await load();
      onRestored?.(confirm.version);
    } catch (e) {
      setState((s) => ({ ...s, error: e?.message || "Khôi phục thất bại" }));
      setConfirm(null);
    } finally {
      setRestoring(false);
    }
  };

  return (
    <>
      <Drawer open={open} onClose={onClose} title="Lịch sử phiên bản" width="max-w-lg">
        <p className="mb-4 text-xs text-admin-textMuted">
          Mỗi lần lưu cài đặt tạo một phiên bản. Bạn có thể so sánh và quay lại bất kỳ phiên bản nào — việc khôi phục cũng được ghi lại thành phiên bản mới nên không mất dữ liệu.
        </p>
        {state.loading ? (
          <div className="space-y-3">
            {[0, 1, 2].map((i) => (
              <Skeleton key={i} className="h-20 rounded-xl" />
            ))}
          </div>
        ) : state.error ? (
          <EmptyState icon={History} title="Không tải được lịch sử" description={state.error} action={<Button size="sm" variant="secondary" onClick={load}>Thử lại</Button>} />
        ) : state.versions.length === 0 ? (
          <EmptyState icon={Upload} title="Chưa có phiên bản nào" description="Lưu cài đặt để bắt đầu ghi lịch sử." />
        ) : (
          <ul className="space-y-3">
            {state.versions.map((v, i) => (
              <VersionRow key={v._id} v={v} isCurrent={i === 0} canRestore={can("settings:write")} onRestore={setConfirm} />
            ))}
          </ul>
        )}
      </Drawer>
      {confirm ? (
        <ConfirmModal
          tone="primary"
          message={`Khôi phục về phiên bản ${confirm.version}?`}
          description="Cài đặt hiện tại sẽ được thay bằng phiên bản này và website cập nhật ngay. Bạn vẫn có thể quay lại phiên bản hiện tại sau đó."
          confirmLabel="Khôi phục"
          loading={restoring}
          onConfirm={doRestore}
          onCancel={() => setConfirm(null)}
        />
      ) : null}
    </>
  );
}
